import { DTOCacheAccessOptions } from "@cervval.js/common-action/src/dtos/dtoCacheRepository";
import { ACTION_CACHE_SERVICE, ActionCacheService } from "@cervval.js/common-action/src/services/cache.service";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { BaseDTO } from "@ehealth_tools/common/src/data/dto/base.dto";
import { ID } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
import { ElementTypes } from "./global.model";

/** Shortcut for get dto list */
export function getDTOList<ET = ElementTypes, ElementTypeName extends keyof ET = keyof ET>(tableName: ElementTypeName, cacheService?: ActionCacheService<ET>): ET[ElementTypeName][] {
    return getCacheService(cacheService).getDTOCacheRepository(tableName).dtoList;
}

/** Shortcut for getDtoById */
export function getDTOById<ET = ElementTypes, ElementTypeName extends keyof ET & string = keyof ET & string>(tableName: ElementTypeName, id: ID, params?: { cacheService?: ActionCacheService<ET>, quiet?: boolean, searchUpToDateId?: boolean }): ET[ElementTypeName] {
    const options: DTOCacheAccessOptions = params?.quiet || params?.searchUpToDateId ? { quiet: params?.quiet, searchUpToDateId: params?.searchUpToDateId } : {};
    return getCacheService(params?.cacheService).getDTOCacheRepository(tableName).getDTOById(id, options);
}


/** Shortcut for findDtoById */
export function findDTOById<ET = ElementTypes, ElementTypeName extends keyof ET = keyof ET & string>(tableName: ElementTypeName, id?: ID, params?: { cacheService?: ActionCacheService<ET>, quiet?: boolean, searchUpToDateId?: boolean }): ET[ElementTypeName] | undefined {
    if (id == null) { return undefined; }
    const options: DTOCacheAccessOptions = params?.quiet || params?.searchUpToDateId ? { quiet: params?.quiet, searchUpToDateId: params?.searchUpToDateId } : {};
    return getCacheService(params?.cacheService).getDTOCacheRepository(tableName).findDTOById(id, options);
}

/** Refresh the DTO using the cache */
export function getFreshDTO<ET = ElementTypes, TableName extends keyof ET & string = keyof ET & string, T extends ET[TableName] & BaseDTO<TableName> = ET[TableName] & BaseDTO<TableName>>(dto: T): T {
    if (dto.__tableName == null) {
        throw new Error("DTO must be typed to be refreshed");
    }
    return getDTOById<ET, TableName>(dto.__tableName, dto.id) as T;

}

/** Get given cache service or cacheService in injector */
export function getCacheService<ET = ElementTypes>(cacheService?: ActionCacheService<ET>): ActionCacheService<ET> {
    return cacheService ?? injector.get<ActionCacheService<ET>>(ACTION_CACHE_SERVICE);
}

/** */
export function getFilteredDTOList<ET = ElementTypes, T extends keyof ET = keyof ET>(tableName: T, filter?: { [K in keyof ET[T]]?: ET[T][K] }, _cacheService?: ActionCacheService<ET>): ET[T][] {
    const cacheService = getCacheService(_cacheService);
    const dtos = cacheService.getDTOCacheRepository(tableName).dtoList;
    if (filter) {
        return dtos.filter(dto => {
            for (const property of objectKeys(filter)) {
                if (dto[property] !== filter[property]) {
                    return false;
                }
            }
            return true;
        });
    }
    return dtos;
}